<template>
  <v-dialog
    ref="dialog"
    v-bind="bindableProps"
    v-model="dialog"
    style="z-index: 9999"
    :content-class="`w-dialog w-dialog-basic ${contentClass}`"
    persistent
    scrollable
    :transition="transition"
  >
    <w-card
      ref="dialogCard"
      class="w-dialog-card"
      :style="dialogCardStyle"
    >
      <slot
        :resolve="triggerResolve"
        :reject="triggerReject"
        :close="close"
      />
      <v-card-title
        v-if="!noHeader"
        ref="dialogHeader"
        class="w-dialog-header with-h-line"
        style="width: auto"
      >
        <div
          class="
            w-dialog-header__wrapper
            d-flex
            justify-space-between
            align-center
          "
          style="width: 100%"
        >
          <div class="header-text-wrapper">
            <slot name="header">
              {{ title === 'default' ? $t('Dialogs.BasicDialog.title') : title }}
            </slot>
          </div>
          <div class="d-flex align-center">
            <slot
              name="header-append"
              :trigger-resolve="triggerResolve"
              :trigger-reject="triggerReject"
              :close="close"
            />
            <w-close-button
              v-if="noSimpleClose === false"
              class="ml-2"
              @click="close"
            >
              <template #tooltip>
                {{ $t('Common.close') }}
              </template>
            </w-close-button>
          </div>
        </div>
      </v-card-title>

      <v-card-text
        ref="dialogContent"
        class="w-dialog-content"
      >
        <div class="dialog-content">
          <slot name="content-wrapper">
            <span>
              <slot
                name="content"
                :remaining-height="remainingHeight"
              />
            </span>
          </slot>
          <slot name="content-append" />
        </div>
      </v-card-text>

      <v-card-actions
        v-if="showActions || $vuetify.breakpoint.xsOnly"
        ref="dialogFooter"
        class="w-dialog-actions with-h-line-top"
        style="width:auto"
      >
        <slot
          v-if="showActions"
          name="dialog-actions"
          :trigger-resolve="triggerResolve"
          :trigger-reject="triggerReject"
          :close="close"
        >
          <slot
            name="abort-action"
            :trigger-resolve="triggerResolve"
            :trigger-reject="triggerReject"
            :close="close"
          >
            <w-btn
              outlined
              @click="close"
            >
              <slot name="abort-action-text">
                {{ $t('Dialogs.BasicDialog.abort') }}
              </slot>
            </w-btn>
          </slot>
          <slot
            name="accept-action"
            :trigger-resolve="triggerResolve"
            :trigger-reject="triggerReject"
            :close="close"
          >
            <w-btn @click="triggerResolve">
              <slot name="accept-action-text">
                {{ acceptActionText === 'default' ? $t('Dialogs.BasicDialog.accept') : acceptActionText }}
              </slot>
            </w-btn>
          </slot>
        </slot>

        <w-btn
          v-if="!showActions && $vuetify.breakpoint.xsOnly"
          @click="mobileCloseHandler"
        >
          {{ $t('Common.close') }}
        </w-btn>
      </v-card-actions>
    </w-card>
  </v-dialog>
</template>

<script>
import { mapActions } from 'vuex';
import GLOBAL_EVENT_LISTENERS from '@/utils/enum/GLOBAL_EVENT_LISTENERS';
import WORK_DIALOG_SIZES from '@/components/base_components/dialog/components/enums/WORK_DIALOG_SIZES';
import { filterProps } from '@/utils/functional-component-utils';
import dialogSizeHandler from '@/components/base_components/dialog/components/mixins/dialog-size-handler';
import i18n from '@/language/i18n';
import {
  isNumber,
} from '@/components/view_root_components/services/custom-comparisons';

export default {
  name: 'WorkBasicDialog',
  props: {
    contentClass: { type: String, default: '' },
    width: {
      type: [String, Number],
      default: WORK_DIALOG_SIZES.MEDIUM,
      validator: (value) => {
        return isNumber(value) || Object.values(WORK_DIALOG_SIZES).includes(value);
      },
    },
    height: {
      type: [String, Number],
      default: WORK_DIALOG_SIZES.MEDIUM,
      validator: (value) => {
        return isNumber(value) || Object.values(WORK_DIALOG_SIZES).includes(value);
      },
    },
    fixedHeight: { type: Boolean, default: false },
    additionToDefaultClose: { type: Function, default: () => {} },
    noSimpleClose: { type: Boolean, default: false },
    showActions: { type: Boolean, default: true },
    title: { type: String, default: 'default' },
    noHeader: { type: Boolean, default: false },
    acceptActionText: { type: String, default: 'default' },
  },
  mixins: [dialogSizeHandler],
  data() {
    return {
      dialog: false,
      oldEscHandler: undefined,
    };
  },
  methods: {
    ...mapActions('globalEventListeners', ['setKeyUpHandlerFor']),
    async open() {
      this.oldEscHandler = await this.setKeyUpHandlerFor({
        key: GLOBAL_EVENT_LISTENERS.ESC,
        handler: this.noSimpleClose ? () => {} : this.close,
      });

      this.dialog = true;
      return new Promise((resolve, reject) => {
        this.resolve = resolve;
        this.reject = reject;
      });
    },
    triggerResolve(value) {
      this.close();
      this.resolve(value);
    },
    triggerReject(value) {
      this.close();
      this.reject(value);
    },
    close() {
      this.dialog = false;
      this.setKeyUpHandlerFor({
        key: GLOBAL_EVENT_LISTENERS.ESC,
        handler: this.oldEscHandler,
      });
      this.additionToDefaultClose();
      this.$emit('close:dialog');
    },
    mobileCloseHandler() {
      if (this.noSimpleClose) {
        return this.triggerReject();
      }
      return this.close();
    },
  },
  computed: {
    bindableProps() {
      return filterProps(this.$props, ['width', 'height']);
    },
    dialogCardStyle() {
      return `max-height: ${this.currentMaxHeight}px; width: ${
        this.currentMaxWidth
      }px; max-width: 95vw; ${
        this.fixedHeight ? 'min-height: ' + this.currentMaxHeight + 'px' : ''
      }`;
    },
    transition() {
      return this.$vuetify.breakpoint.xsOnly ? false : 'dialog-transition'
    }
  },
  beforeDestroy() {}
};
</script>
<style lang="scss" scoped>
@import '~vuetify/src/styles/settings/_variables';

::v-deep .w-dialog.w-dialog-basic {
  overflow-y: hidden;
  max-height: 100%;
  width: auto;
  height: auto;
}

.w-dialog .w-dialog-card {
  display: flex;
  flex-direction: column;
  padding: 0;
  margin: 0;
}

.w-dialog .w-dialog-card .w-dialog-header {
  background-color: $dialog-bg-color;
  color: $dialog-color;
  padding: $dialog-default-padding;
  font-weight: bold;
}

.w-dialog .w-dialog-card .w-dialog-content {
  flex: 1;

  color: $dialog-color;
  padding: $dialog-content-padding;
  font-size: 1rem;
  display: flex;
  flex-flow: wrap;
  overflow-y: auto;

  .dialog-content {
    width: 100%;
    max-height: 100%;

    ::v-deep .w-unit, 
    ::v-deep .w-base-table-look-a-like-unit, 
    ::v-deep .w-table-unit 
    {
      border-color: $workflow-unit-border-color !important;
    }
  }
}

.w-dialog .w-dialog-card .w-dialog-actions {
  padding: $dialog-actions-padding;
  background-color: $dialog-bg-color;

  justify-content: flex-end;
  display: flex;
  flex-flow: wrap;
}
.w-dialog-actions > ::v-deep.v-btn {
  margin-bottom: $dialog-actions-margin-bottom;
}

.w-table-dialog .w-dialog-content .dialog-content {
  height: 100%;
}

@media #{map-get($display-breakpoints, 'xs-only')} {
  ::v-deep .w-dialog.w-dialog-basic {
    overflow-y: unset;
  }

  .v-dialog.w-dialog.w-dialog-basic .w-dialog-card {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    max-height: 100vh !important;
    width: 100vw !important;
    max-width: 100vw !important;
  }
}
</style>
